import Link from '@components/elements/Link';
import ROUTE_URLS from '@constants/routeUrls';
import TAB_SLUGS from '@constants/tabSlugs';
import useAppConfig from '@hooks/useAppConfig';
import useAppMode from '@hooks/useAppMode';
import { pushClickMenuGTMEvent } from '@utils/gtm';
import { toUrl } from '@utils/helpersTS';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

type OrderLinksProps = {
  secondaryButtonVariant?: 'text' | 'outlined';
};

type LinkProps = {
  variant: 'text' | 'outlined' | 'contained';
  href?: string | null;
};

const DietsLink = ({ variant, href }: LinkProps) => {
  const { t } = useTranslation();

  return (
    <Link
      href={
        href ??
        ROUTE_URLS.NEW_ORDER_FORM_STEP({
          step: TAB_SLUGS.SELECT_DIET,
        })
      }
      type="button"
      variant={variant}
      data-cy="nav__order_diets"
      onClick={() => {
        pushClickMenuGTMEvent({
          text: t('$*common.orderDiet'),
        });
      }}
    >
      {t('$*common.orderDiet')}
    </Link>
  );
};

const DishesLink = ({ variant, href }: LinkProps) => {
  const { t } = useTranslation();

  return (
    <Link
      href={
        href ??
        ROUTE_URLS.NEW_ORDER_FORM_STEP({
          step: TAB_SLUGS.SELECT_DISHES,
        })
      }
      data-cy="nav__order_dishes"
      type="button"
      variant={variant}
      onClick={() => {
        pushClickMenuGTMEvent({
          text: t('$*common.orderDishes'),
        });
      }}
    >
      {t('$*common.orderDishes')}
    </Link>
  );
};

const OrderLinks = ({ secondaryButtonVariant }: OrderLinksProps) => {
  const router = useRouter();

  const {
    data: {
      branding: { mainDomainDietPanel, mainDomainShopPanel },
    },
  } = useAppConfig();
  const { isDiet, isOnlyShop } = useAppMode();

  if (router.pathname.includes(ROUTE_URLS.NEW_ORDER_FORM)) {
    return null;
  }

  const secondaryVariant =
    secondaryButtonVariant === 'outlined' ? 'outlined' : 'text';

  const showDietLink = isDiet || mainDomainDietPanel;
  const showDishesLink = isOnlyShop || mainDomainShopPanel;

  return isDiet ? (
    <>
      {showDietLink && <DietsLink variant="contained" />}
      {showDishesLink && (
        <DishesLink
          variant={secondaryVariant}
          href={toUrl(mainDomainShopPanel)}
        />
      )}
    </>
  ) : (
    <>
      {showDishesLink && <DishesLink variant="contained" />}
      {showDietLink && (
        <DietsLink
          variant={secondaryVariant}
          href={toUrl(mainDomainDietPanel)}
        />
      )}
    </>
  );
};

export default OrderLinks;
